import { CertificationTooltipComponent, CertifierProps } from '@tableau/certification-watermark';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';


import { Messages } from '../../../../gen-src/Localize';
import { DatabaseCertificationFragment, TableCertificationFragment } from '../../../../gen-src/queries';
import {
  getUserNameForDisplay,
  getUserUrlForDisplay,
  isUserActive,
  isUserInfoVisible,
  TableauUser,
  UserPlaceholderStyle
} from './UserDisplayComponent';
import { FormattedMarkupWrapperInTooltip } from './style/MarkupStyles';

export type CertificationWidgetProps = {
  readonly icon: JSX.Element
  readonly contentId: string;
  readonly certification: DatabaseCertificationFragment | TableCertificationFragment;
  readonly siteUrls: SiteUrls;
  readonly showExtractRefresh: boolean;
  readonly className?: string;
};

/**
 * Converts a user into the "certifier props" for the certification widget.
 * In some cases (e.g. the Connect To dialog), linking is disabled, so if SiteUrls is not passed in,
 * it will not include the URL prop, allowing the widget to render the certifier's name as static text.
 * Inactive users will also be rendered as static text, because they don't have a URL.
 * @param certifier
 * @param siteUrls
 */
export function getCertifierPropsFromCertifier(certifier: TableauUser, siteUrls?: SiteUrls): CertifierProps {
  if (!isUserInfoVisible(certifier)) {
    return {
      customUserStyle: UserPlaceholderStyle,
      displayName: Messages.HiddenUser(),
    };
  } else if (!isUserActive(certifier)) {
    return {
      displayName: getUserNameForDisplay(certifier),
    };
  } else {
    return {
      url: siteUrls ? getUserUrlForDisplay(siteUrls, certifier) : undefined,
      displayName: getUserNameForDisplay(certifier),
    };
  }
}

/**
 * Displays a certification icon, which can then be clicked to launch a tooltip giving more details.
 * If the item is not certified, will render null.
 * @param props
 */
export function CertificationWidget(props: CertificationWidgetProps): JSX.Element | null {

  const certifier = props.certification.certifier;
  if (!props.certification.isCertified || !certifier) {
    return null;
  }

  const certificationNote = props.certification.certificationNote || '';

  return (
    <CertificationTooltipComponent
      id={props.contentId}
      icon={props.icon}
      note={<FormattedMarkupWrapperInTooltip markup={certificationNote} />}
      certifier={getCertifierPropsFromCertifier(certifier, props.siteUrls)}
      className={props.className}
      showExtractRefresh={props.showExtractRefresh}
    />
  );
}
